import { faBug, faEnvelope, faThumbsUp } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, TextField, Typography } from "@mui/material"
import { alpha, Theme } from "@mui/system"
import { historySVC } from "app/history/service"
import { translate } from "app/language/service"
import { LOGIN_PATH, SIGNUP_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React, { useMemo, useState } from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { Logo } from "ui/components/Logo"
import { getUiConfigLoginImageSrc } from "ui/configuration/tools/common"
import { MAX_TABLET_WIDTH_IN_PX } from "ui/model"
import { FORGOT_PASSWORD_PAGE_ID, SIGN_UP_VERIFICATION_PAGE_ID } from "user/model"
import { isValidEmail } from "user/tools/common"
import { resetPasswordDemand } from "user/tools/password"
import { LoginPageShiningSquares } from "./LoginPageShiningSquares"

export const ForgotPasswordPage = () => {
  const [isRequesting, setIsRequesting] = useState(false)
  const [email, setEmail] = useState(historySVC.getHashParameter("email"))
  const [touched, setTouched] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [displaySuccess, setDisplaySuccess] = useState(false)
  const isValid = useMemo(() => isValidEmail(email), [email])
  React.useEffect(() => {
    const script = document.createElement("script")
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_TOKEN}`
    script.async = true
    document.body.appendChild(script)
  }, [])
  const backgroundSx: any = {}
  const imageSrc = getUiConfigLoginImageSrc()
  if (imageSrc) {
    backgroundSx.backgroundImage = `url(${imageSrc})`
    backgroundSx.backgroundRepeat = "no-repeat"
    backgroundSx.backgroundPosition = "center"
    backgroundSx.backgroundSize = "cover"
  } else {
    backgroundSx.backgroundColor = (theme: Theme) => theme.palette.primary.main
    backgroundSx.background = (theme: Theme) =>
      `linear-gradient(90deg, ${alpha(theme.palette.primary.main as string, 0.75)} 0%, ${
        theme.palette.primary.main
      } 100%)`
  }
  const onSubmit = async (captchaToken: string) => {
    try {
      setIsRequesting(true)
      await resetPasswordDemand(email, captchaToken)
      setIsRequesting(false)
      setDisplaySuccess(true)
    } catch (error) {
      console.error(error)
      setIsRequesting(false)
      setHasError(true)
    }
  }
  const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    if (event.key === "Enter" && isValid) {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_TOKEN, { action: "submit" })
          .then((token: string) => {
            onSubmit(token)
          })
          .catch((error: any) => {
            console.error("Unable to get recaptcha token", error)
            setHasError(true)
          })
      })
    }
  }
  return (
    <FlexBox
      id={SIGN_UP_VERIFICATION_PAGE_ID}
      vertical
      fullWidth
      sx={{
        "position": "relative",
        "flexDirection": "column",
        "height": "100%",
        "padding": 0,
        "margin": 0,
        "boxShadow": "0 0.125rem 0.5rem 0 rgba(0, 0, 0, 0.2)",
        "backgroundColor": theme => theme.palette.background.paper,
        "& .code-version": {
          marginBottom: "1rem"
        }
      }}>
      {!imageSrc && <LoginPageShiningSquares />}
      <FlexBox
        sx={{
          ...backgroundSx,
          height: "calc(100% - 10.5rem)",
          minHeight: "calc(100% - 10.5rem)",
          maxHeight: "calc(100% - 10.5rem)",
          width: "calc(100% - 10.5rem)",
          minWidth: "calc(100% - 10.5rem)",
          maxWidth: "calc(100% - 10.5rem)",
          overflow: "hidden",
          [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%"
          }
        }}
        id={`${SIGN_UP_VERIFICATION_PAGE_ID}-background`}>
        <FlexBox
          gap=""
          vertical
          jc="space-evenly"
          sx={{
            "backgroundColor": theme => theme.palette.background.default,
            "p": "2rem",
            "width": "35rem",
            "minWidth": "35rem",
            "maxWidth": "35rem",
            "height": "30rem",
            "minHeight": "30rem",
            "maxHeight": "30rem",
            "pl": "3rem",
            "pr": "3rem",
            "zIndex": "1",
            "& .MuiFormControl-root": {
              borderRadius: ".375rem"
            },
            "& input": {
              px: ".4rem"
            },
            [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%"
            }
          }}
          id={`${SIGN_UP_VERIFICATION_PAGE_ID}-form`}>
          <FlexBox vertical>
            <Logo id={`${SIGN_UP_VERIFICATION_PAGE_ID}-logo`} sx={{ minHeight: "5rem" }} />
            <FlexBox gap>
              <Typography
                id={`${FORGOT_PASSWORD_PAGE_ID}-login-title`}
                color="textSecondary"
                onClick={() => navigate(LOGIN_PATH)}
                sx={{
                  "textTransform": "uppercase",
                  "&:hover": {
                    cursor: "pointer",
                    color: theme => theme.palette.primary.main
                  }
                }}>
                {translate("login.signin")}
              </Typography>
              <Divider orientation="vertical" sx={{ height: "1rem" }} />
              <Typography
                id={`${FORGOT_PASSWORD_PAGE_ID}-signup-title`}
                color="textSecondary"
                onClick={() => navigate(SIGNUP_PATH)}
                sx={{
                  "textTransform": "uppercase",
                  "&:hover": {
                    cursor: "pointer",
                    color: theme => theme.palette.primary.main
                  }
                }}>
                {translate("login.signup")}
              </Typography>
            </FlexBox>
          </FlexBox>
          {hasError ? (
            <FlexBox vertical gap sx={{ p: "2rem" }}>
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
                <FontAwesomeIcon icon={faBug} size="3x" className="icon" />
                <Typography color="error" textAlign="justify">
                  {translate("user.login.error.unexpected")}
                </Typography>
              </FlexBox>
              <Button onClick={() => navigate(LOGIN_PATH)} variant="contained">
                {translate("login.title")}
              </Button>
            </FlexBox>
          ) : displaySuccess ? (
            <FlexBox vertical gap sx={{ p: "2rem" }}>
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
                <FontAwesomeIcon icon={faThumbsUp} size="3x" className="icon" />
                <Typography id="forgot-password-success-message" color="textPrimary" textAlign="justify">
                  {translate("user.password.reset.confirmed")}
                </Typography>
              </FlexBox>
              <Button id="forgot-password-login-button" onClick={() => navigate(LOGIN_PATH)} variant="contained">
                {translate("login.title")}
              </Button>
            </FlexBox>
          ) : isRequesting ? (
            <LinearLoader />
          ) : (
            <FlexBox id="forgot-password-form" vertical gap="2rem" sx={{ p: "2rem" }}>
              <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.primary.main }, "width": "21rem" }}>
                <FontAwesomeIcon icon={faEnvelope} size="3x" className="icon" />
                <Typography id="forgot-password-message" color="textPrimary" textAlign="justify">
                  {translate("user.password.reset.email")}
                </Typography>
              </FlexBox>
              <FlexBox vertical gap>
                <TextField
                  id="forgot-password-email-field"
                  label={translate("label.email")}
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  onBlur={() => {
                    if (!touched) {
                      setTouched(true)
                    }
                  }}
                  onKeyUp={event => onKeyUp(event)}
                  error={!isValid && touched}
                  sx={{ width: "21rem" }}
                />
                {!isValid && touched && (
                  <Typography
                    id="forgot-password-email-error"
                    color="error"
                    fontSize=".9rem"
                    textAlign="justify"
                    sx={{ mb: "-1rem", width: "100%" }}>
                    {translate("label.invalid.email")}
                  </Typography>
                )}
              </FlexBox>
              <FlexBox fullWidth wrap gap ai="flex-end" sx={{ "& > *": { width: "10rem" } }}>
                <Button onClick={() => navigate(LOGIN_PATH)} variant="contained">
                  {translate("button.cancel")}
                </Button>
                <Button
                  id="forgot-password-submit"
                  disabled={!isValid}
                  onClick={event => {
                    event.preventDefault()
                    grecaptcha.ready(() => {
                      grecaptcha
                        .execute(RECAPTCHA_TOKEN, { action: "submit" })
                        .then((token: string) => {
                          onSubmit(token)
                        })
                        .catch((error: any) => {
                          console.error("Unable to get recaptcha token", error)
                          setHasError(true)
                        })
                    })
                  }}
                  variant="contained">
                  {translate("button.ok")}
                </Button>
              </FlexBox>
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}
